var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { useMemo } from "react";
import moment from "moment";
import { formatUnits } from "@ethersproject/units";
import { getAssets, VaultFees, VaultList, VaultVersionList, } from "../constants/constants";
import useVaultPriceHistory, { useVaultsPriceHistory, } from "./useVaultPerformanceUpdate";
import { getAssetDecimals } from "../utils/asset";
import useYearnAPIData from "./useYearnAPIData";
import useLidoAPY from "./useLidoOracle";
import { useV2VaultData, useV2VaultsData } from "./web3DataContext";
import { apyFromPricePerShare } from "../utils/math";
/**
 *
 * @param priceHistory
 * @param periodStart
 * @descpipt 获取历史 price
 * @returns
 */
var getPriceHistoryFromPeriod = function (priceHistory, periodStart) {
    /**
     * Does not procees if price history length is lesser than or equal to 2
     */
    // console.log("priceHistory:::", priceHistory)
    if (priceHistory.length <= 2) {
        /**
         * If only 2 points, return them
         */
        if (priceHistory.length === 2) {
            return [priceHistory[0], priceHistory[1]];
        }
        return undefined;
    }
    var _a = [
        periodStart.unix(),
        periodStart.clone().add(1, "week").unix(),
        periodStart.clone().add(2, "week").unix(),
    ], periodStartTimestamp = _a[0], periodEndTimestamp = _a[1], nextPeriodEndTimestamp = _a[2];
    // console.log("priceHistory>", priceHistory)
    var priceHistoryInPeriod = priceHistory.filter(//  within a week
    function (historyItem) {
        return historyItem.timestamp >= periodStartTimestamp &&
            historyItem.timestamp < periodEndTimestamp;
    });
    var priceHistoryInNextPeriod = priceHistory.filter(// Within the last two weeks
    function (historyItem) {
        return historyItem.timestamp >= periodEndTimestamp &&
            historyItem.timestamp < nextPeriodEndTimestamp;
    });
    // console.log("priceHistoryInPeriod:", priceHistoryInPeriod);
    // console.log("priceHistoryInNextPeriod:", priceHistoryInNextPeriod);
    /**
     * Check if one of the period is empty
     */
    if (!priceHistoryInPeriod.length || !priceHistoryInNextPeriod.length) {
        /**
         * If combined length is more than or equals to 2, we simply return the first and second
         */
        // console.log("priceHistoryInPeriod:::", priceHistoryInPeriod, priceHistoryInNextPeriod)
        if (priceHistoryInPeriod.length + priceHistoryInNextPeriod.length >= 2) {
            var combinedPriceHistory = __spreadArray(__spreadArray([], priceHistoryInPeriod), priceHistoryInNextPeriod);
            return [combinedPriceHistory[0], combinedPriceHistory[1]];
        }
        /**
         * Else, return undefined
         */
        return undefined;
    }
    return [priceHistoryInPeriod[0], priceHistoryInNextPeriod[0]];
};
/**
 * To calculate APY, we first find a week that has not been exercised.
 * This is to remove accounting of losses into calculating APY.
 * The calculation shall be calculated based on the start of previous period versus the the start of current period.
 * Each period consist of a week, starting from a friday UTC 10am until the next friday UTC 10am.
 */
export var calculateAPYFromPriceHistory = function (priceHistory, decimals, _a, underlyingYieldAPR) {
    // console.group('calculateAPYFromPriceHistory')
    var _b, _c;
    var vaultOption = _a.vaultOption, vaultVersion = _a.vaultVersion;
    // console.log('priceHistory:', priceHistory)
    // console.log('decimals:', decimals)
    // console.log('vaultOption:', vaultOption)
    // console.log('vaultVersion:', vaultVersion)
    // console.log('underlyingYieldAPR:', underlyingYieldAPR)
    var periodStart = moment()
        .isoWeekday("friday")
        .utc()
        .set("hour", 10)
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0);
    if (periodStart.isAfter(moment())) {
        periodStart.subtract(1, "week");
    }
    // console.log(`------------${vaultOption}-----------------`)
    // console.log('periodStart:', periodStart.format("YYYY/MM/DD HH:MM:SS"));
    var weeksAgo = 0;
    var apys = [];
    while (true) {
        // console.log(`-------${weeksAgo}--------`)
        var priceHistoryFromPeriod = getPriceHistoryFromPeriod(//  筛选出近一周和近两周的交易数据
        priceHistory, periodStart);
        // console.log('priceHistoryFromPeriod:', priceHistoryFromPeriod)
        if (priceHistoryFromPeriod) {
            var _d = [
                parseFloat(formatUnits(priceHistoryFromPeriod[0].pricePerShare, decimals)),
                parseFloat(formatUnits(priceHistoryFromPeriod[1].pricePerShare, decimals)),
            ], startingPricePerShare = _d[0], endingPricePerShare = _d[1];
            // Only includes weeks that is positive
            if (endingPricePerShare > startingPricePerShare) {
                if (weeksAgo > 0) {
                    // console.log('startingPricePerShare:', startingPricePerShare)
                    // console.log('endingPricePerShare:', endingPricePerShare)
                    /**
                     * Fees and underlying yields had been accounted
                     */
                    apys.push(apyFromPricePerShare(startingPricePerShare, endingPricePerShare) +
                        underlyingYieldAPR);
                }
                else {
                    switch (vaultVersion) {
                        case "v1":
                            // console.log('startingPricePerShareV1:', startingPricePerShare)
                            // console.log('endingPricePerShareV1:', endingPricePerShare)
                            /**
                             * V1 does not have fees that can impact performance
                             */
                            apys.push(apyFromPricePerShare(startingPricePerShare, endingPricePerShare) + underlyingYieldAPR);
                            break;
                        case "v2":
                            /**
                             * We first calculate price per share after annualized management fees are charged
                             */
                            // console.log('-----inot V2------')
                            var endingPricePerShareAfterManagementFees = endingPricePerShare *
                                (1 -
                                    parseFloat((_b = VaultFees[vaultOption].v2) === null || _b === void 0 ? void 0 : _b.managementFee) /
                                        100 /
                                        52);
                            /**
                             * Next, we calculate how much performance fees will lower the pricePerShare
                             */
                            var performanceFeesImpact = (endingPricePerShare - startingPricePerShare) *
                                (parseFloat((_c = VaultFees[vaultOption].v2) === null || _c === void 0 ? void 0 : _c.performanceFee) / 100);
                            /**
                             * Finally, we calculate price per share after both fees
                             */
                            var pricePerShareAfterFees = endingPricePerShareAfterManagementFees - performanceFeesImpact;
                            // console.group("pricePerShareAfterFees")
                            // console.log('startingPricePerShare:', startingPricePerShare)
                            // console.log('pricePerShareAfterFees:', pricePerShareAfterFees)
                            // console.log('endingPricePerShareAfterManagementFees:', endingPricePerShareAfterManagementFees)
                            // console.log('performanceFeesImpact:', performanceFeesImpact)
                            // console.log('endingPricePerShare:', endingPricePerShare)
                            // console.log('VaultFees[vaultOption].v2?.performanceFee!:', VaultFees[vaultOption])
                            // console.groupEnd();
                            apys.push(apyFromPricePerShare(startingPricePerShare, pricePerShareAfterFees) + underlyingYieldAPR);
                            // console.log("apys>>>", apys)
                            break;
                    }
                }
            }
        }
        // Continue to previous week
        periodStart.subtract(1, "week");
        weeksAgo += 1;
        /**
         * Prevent searching too far ago
         */
        if (weeksAgo >= 4) {
            break;
        }
    }
    // console.log('apys:', apys)
    // console.log(apys.reduce((acc, curr) => acc + curr, 0) / apys.length || 0);
    // console.groupEnd()
    // Calculate average apy
    return apys.reduce(function (acc, curr) { return acc + curr; }, 0) / apys.length || 0;
};
export var useLatestAPY = function (vaultOption, vaultVersion) {
    var _a = useVaultPriceHistory(vaultOption, vaultVersion), priceHistory = _a.priceHistory, vaultPriceHistoryLoading = _a.loading;
    var _b = useV2VaultData(vaultOption), _c = _b.data, round = _c.round, pricePerShare = _c.pricePerShare, vaultDataLoading = _b.loading;
    var loading = vaultPriceHistoryLoading || vaultDataLoading;
    // const { getVaultAPR } = useYearnAPIData();
    // const lidoAPY = useLidoAPY();
    var underlyingYieldAPR = 0;
    // switch (vaultOption) {
    //   case "ryvUSDC-ETH-P-THETA":
    //     underlyingYieldAPR = getVaultAPR("yvUSDC", "0.3.0") * 100;
    //     break;
    //   case "rstETH-THETA":
    //     underlyingYieldAPR = lidoAPY * 100;
    //     break;
    // }
    // console.group('useLatestAPY')
    // console.log('vaultPriceHistoryLoading:', vaultPriceHistoryLoading)
    // console.log('vaultDataLoading:', vaultDataLoading)
    // console.log('loading:', loading)
    // console.log('getVaultAPR:', getVaultAPR)
    // console.log('lidoAPY:', lidoAPY)
    // console.groupEnd()
    return {
        fetched: !loading,
        res: loading
            ? 0
            : calculateAPYFromPriceHistory(priceHistory.map(function (history) {
                return history.round === round ? __assign(__assign({}, history), { pricePerShare: pricePerShare }) : history;
            }), getAssetDecimals(getAssets(vaultOption)), { vaultOption: vaultOption, vaultVersion: vaultVersion }, underlyingYieldAPR),
    };
};
export default useLatestAPY;
export var useLatestAPYs = function () {
    var _a = useVaultsPriceHistory(), vaultsPriceHistory = _a.data, vaultsPriceHistoryLoading = _a.loading;
    var _b = useV2VaultsData(), vaultsData = _b.data, vaultsDataLoading = _b.loading;
    var loading = vaultsPriceHistoryLoading || vaultsDataLoading;
    var getVaultAPR = useYearnAPIData().getVaultAPR;
    var lidoAPY = useLidoAPY();
    var latestAPYs = useMemo(function () {
        return Object.fromEntries(VaultVersionList.map(function (version) { return [
            version,
            Object.fromEntries(VaultList.map(function (vaultOption) {
                var priceHistory = vaultsPriceHistory[version][vaultOption];
                var _a = vaultsData[vaultOption], round = _a.round, pricePerShare = _a.pricePerShare;
                switch (version) {
                    case "v2":
                        switch (vaultOption) {
                        }
                }
                var underlyingYieldAPR = 0;
                // switch (vaultOption) {
                //   case "ryvUSDC-ETH-P-THETA":
                //     underlyingYieldAPR = getVaultAPR("yvUSDC", "0.3.0") * 100;
                //     break;
                //   case "rstETH-THETA":
                //     underlyingYieldAPR = lidoAPY * 100;
                // }
                return [
                    vaultOption,
                    loading
                        ? 0
                        : calculateAPYFromPriceHistory(priceHistory.map(function (history) {
                            return history.round === round
                                ? __assign(__assign({}, history), { pricePerShare: pricePerShare }) : history;
                        }), getAssetDecimals(getAssets(vaultOption)), { vaultOption: vaultOption, vaultVersion: version }, underlyingYieldAPR),
                ];
            })),
        ]; }));
    }, [vaultsData, vaultsPriceHistory, getVaultAPR, lidoAPY, loading]);
    return { fetched: !loading, res: latestAPYs };
};
//# sourceMappingURL=useLatestAPY.js.map